import { useState } from 'react'
import { Card, Table, Button, Tag, Space, Input, Modal, Form, Select, message, Avatar } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, LockOutlined } from '@ant-design/icons'
import type { User, UserRole } from '../../types/index'
import { mockUsers } from '../../services/mockData'

const { Option } = Select

const UserManagement = () => {
  const [users, setUsers] = useState<User[]>(mockUsers)
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [form] = Form.useForm()
  const [searchText, setSearchText] = useState('')

  const roleLabels: Record<UserRole, { label: string; color: string }> = {
    admin: { label: '管理员', color: 'red' },
    annotator: { label: '标注员', color: 'blue' },
    reviewer: { label: '审核员', color: 'green' },
    query_user: { label: '信息查询员', color: 'purple' },
  }

  const columns = [
    {
      title: '用户',
      key: 'user',
      render: (_: any, record: User) => (
        <Space>
          <Avatar icon={record.username[0].toUpperCase()} />
          <div>
            <div className="font-medium">{record.username}</div>
            <div className="text-xs text-gray-400">{record.email}</div>
          </div>
        </Space>
      ),
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: UserRole) => (
        <Tag color={roleLabels[role].color}>{roleLabels[role].label}</Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'success' : 'default'}>
          {status === 'active' ? '🟢 在职' : '🔴 离职'}
        </Tag>
      ),
    },
    {
      title: '案件数',
      key: 'cases',
      render: (_: any, record: User) => record.stats?.totalCases || '-',
    },
    {
      title: '准确率',
      key: 'accuracy',
      render: (_: any, record: User) => 
        record.stats?.accuracy ? `${record.stats.accuracy}%` : '-',
    },
    {
      title: '最近登录',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: User) => (
        <Space>
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button 
            type="text" 
            icon={<LockOutlined />}
            onClick={() => message.info('重置密码功能')}
          >
            重置密码
          </Button>
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            {record.status === 'active' ? '禁用' : '启用'}
          </Button>
        </Space>
      ),
    },
  ]

  const handleAdd = () => {
    setEditingUser(null)
    form.resetFields()
    setModalVisible(true)
  }

  const handleEdit = (user: User) => {
    setEditingUser(user)
    form.setFieldsValue(user)
    setModalVisible(true)
  }

  const handleDelete = (user: User) => {
    Modal.confirm({
      title: `确认${user.status === 'active' ? '禁用' : '启用'}该用户？`,
      content: `用户: ${user.username}`,
      onOk: () => {
        message.success('操作成功')
      },
    })
  }

  const handleSave = () => {
    form.validateFields().then(values => {
      message.success(editingUser ? '更新成功' : '创建成功')
      setModalVisible(false)
    })
  }

  const filteredUsers = users.filter(u => 
    u.username.toLowerCase().includes(searchText.toLowerCase()) ||
    u.email.toLowerCase().includes(searchText.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">👥 用户管理</h1>
        <Space>
          <Input
            prefix={<SearchOutlined />}
            placeholder="搜索用户..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新增用户
          </Button>
        </Space>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingUser ? '编辑用户' : '新增用户'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[{ required: true, message: '请输入邮箱' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select>
              <Option value="admin">管理员</Option>
              <Option value="annotator">标注员</Option>
              <Option value="reviewer">审核员</Option>
              <Option value="query_user">信息查询员</Option>
            </Select>
          </Form.Item>
          {!editingUser && (
            <Form.Item
              name="password"
              label="初始密码"
              rules={[{ required: true, message: '请输入初始密码' }]}
            >
              <Input.Password />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  )
}

export default UserManagement
